// One derivation per change, for the whole screen.
//
// `src/derive/derive.ts` is the pure half: an object in, an object out, no
// clock of its own. This is the half that is wired to the running app — the
// application node. It subscribes to the three things a derivation depends on
// (the snapshot, the view, the centre), reads `Date.now()` ONCE per change and
// pushes the result to whoever draws.
//
// **The one clock.** The rail, the wheel and „Warstwy" used to read
// `Date.now()` each, so three surfaces could straddle a minute boundary and
// disagree about the same instant (#244, `verify/wheel.md` §7). The clock is
// read here, rides out on `now`, and a view that reads it again is the defect
// this module exists to remove.
//
// **One change, one derivation.** A `tu=` change notifies `viewState` AND
// `onCentreChange` — the latter registers its own `viewState.subscribe`
// (`state/centre.ts:43`) — so the naive wiring derived twice and every view
// rendered twice (`verify/wheel.md` §5). Both notifications land here, and the
// second one finds the inputs it would derive from unchanged and returns.
// Coalescing on the inputs rather than on a flag is what makes the order the
// two subscribers happen to run in irrelevant.
//
// A push carries no promise that anything changed in the RESULT: the inputs
// changed, and that is what a view redraws for. Diffing the derivation would
// buy a rebuild and cost the guarantee that a view's DOM follows the address.
import { onSnapshot, type Snapshot } from './data/data.ts';
import { derive, type Derived } from './derive/derive.ts';
import { onCentreChange, resolveCentre, type ResolvedCentre } from './state/centre.ts';
import { viewState, type ViewState } from './state/viewstate.ts';

export type DerivedState = {
  /**
   * The derivation itself — `null` until the first snapshot, because there is
   * nothing to derive from no data. The wheel still paints its camera, its
   * rings and its slider in that window, which is why this is a field and not
   * a reason to withhold the push.
   */
  derived: Derived | null;
  /** The snapshot the derivation was built from; `null` before the first poll answers. */
  snapshot: Snapshot | null;
  /** The view it was built for. A consumer reads this instead of `viewState.get()`. */
  view: ViewState;
  /** Where it was measured from. A consumer reads this instead of `resolveCentre()`. */
  centre: ResolvedCentre;
  /** The ONE clock of this change. The only `Date.now()` the derivation knows. */
  now: number;
};

let snapshot: Snapshot | null = null;

const sameCentre = (a: ResolvedCentre, b: ResolvedCentre) =>
  a.lat === b.lat && a.lon === b.lon && a.name === b.name;

function build(view: ViewState, centre: ResolvedCentre): DerivedState {
  const now = Date.now();
  return {
    derived: snapshot ? derive({ snapshot, view, centre, now }) : null,
    snapshot,
    view,
    centre,
    now,
  };
}

const firstView = viewState.get();
let current: DerivedState = build(firstView, resolveCentre(firstView));

const listeners = new Set<(state: DerivedState) => void>();

/**
 * One subscriber's mistake is its own — the stance `src/data/data.ts` and
 * `src/state/viewstate.ts` both take. A rail that throws may not leave the
 * wheel behind it undrawn.
 *
 * A copy, because a subscriber may subscribe or dispose from inside its own
 * callback: iterating the live Set would visit a newcomer that `subscribe`
 * has already handed the same state, and `has` keeps a disposal mid-push
 * honest.
 */
function tell(fn: (state: DerivedState) => void, state: DerivedState) {
  try {
    fn(state);
  } catch (e) {
    console.error('derived: a subscriber threw on a derivation', e);
  }
}

function announce() {
  const listening = [...listeners];
  for (const fn of listening) {
    if (!listeners.has(fn)) continue;
    // `current`, read per subscriber and never hoisted — the other half of the
    // stance `src/state/viewstate.ts` takes, and what made the claim above true
    // rather than half true (PR #274, finding 1). A subscriber that writes
    // `viewState` from inside its push re-enters `recompute`, which assigns a
    // newer `current`; everyone after it must be handed that one, not the state
    // this loop started with.
    tell(fn, current);
  }
}

/**
 * Derives and pushes, unless the three inputs are the ones the standing
 * derivation was built from — which is how one `tu=` change arriving down two
 * subscriptions produces one derivation and one push.
 */
function recompute() {
  const view = viewState.get();
  const centre = resolveCentre(view);
  if (snapshot === current.snapshot && view === current.view && sameCentre(centre, current.centre)) {
    return;
  }
  current = build(view, centre);
  announce();
}

// Registered at import, before any view mounts, so that every view already has
// the derivation of the change it is about to be told of. The three are the
// whole input surface: `onCentreChange` composes the view and the browser's
// answer about where the resident is, and diffing them is its job, not ours.
//
// The guarantee does not rest on the order `main.ts` lists its imports in: ES
// modules evaluate a dependency's body before its dependent's, and every other
// subscriber in the tree registers inside a `mount*()` that `main.ts`'s body
// calls after the whole graph has evaluated. ONE thing would break it — a
// module that registers a `viewState`, `onSnapshot` or `here` subscriber at
// MODULE-EVAL time and evaluates before this file. There is no such module
// today (PR #274, finding 2), and adding one is the change to look for.
viewState.subscribe(recompute);
onCentreChange(recompute);
onSnapshot((next) => {
  snapshot = next;
  recompute();
});

export const derived = {
  /**
   * The standing derivation, for a redraw that is not a change: a screen
   * repainted because its own filter was typed into, a rail rebuilt because
   * the fold was opened. Never `null` — the fields inside it may be.
   */
  get: (): DerivedState => current,
  /**
   * Called on every change, and once at once with what is standing — the same
   * contract `onSnapshot` and `theme.subscribe` keep, so a view that mounts
   * late has nothing to miss. That first call is guarded like every other: a
   * view that throws on it is a bug in us, and it may not take down the mount
   * that subscribed it. Returns the disposer, like every other store.
   */
  subscribe(fn: (state: DerivedState) => void): () => void {
    listeners.add(fn);
    tell(fn, current);
    return () => {
      listeners.delete(fn);
    };
  },
};
